import React from 'react';
import { FiBriefcase, FiMapPin, FiDollarSign, FiClock, FiUser, FiMail, FiCalendar, FiBookOpen, FiAward, FiMessageSquare, FiUsers, FiExternalLink } from 'react-icons/fi';
import { PATHS } from '../utils/platformPaths';

/**
 * Mobile card component for alumni directory listing
 */
export const MobileAlumniCard = ({ alumni, onViewProfile, onConnect, onMessage }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4">
      {/* Header with name and photo */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-base truncate">
            {alumni.first_name} {alumni.last_name}
          </h3>
          {alumni.show_current_position && alumni.current_position && (
            <p className="text-sm text-gray-600 mt-1 truncate">{alumni.current_position}</p>
          )}
          {alumni.show_current_employer && alumni.current_employer && (
            <div className="flex items-center mt-1">
              {alumni.current_company_logo && (
                <img
                  src={alumni.current_company_logo}
                  alt={alumni.current_employer}
                  className="h-3 w-3 rounded mr-1 bg-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <p className="text-sm text-blue-600 font-medium truncate">{alumni.current_employer}</p>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 ml-3">
          {alumni.headshot_url ? (
            <img 
              src={alumni.headshot_url} 
              alt={`${alumni.first_name} ${alumni.last_name}`}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
              <FiUser className="w-6 h-6 text-gray-600" />
            </div>
          )}
        </div>
      </div>

      {/* Alumni details with privacy checks */}
      <div className="space-y-1 mb-3 text-xs text-gray-500">
        {alumni.show_graduation_year && alumni.graduation_year && (
          <div className="flex items-center">
            <FiCalendar className="w-3 h-3 mr-1 flex-shrink-0" />
            <span>Class of {alumni.graduation_year}</span>
          </div>
        )}
        {alumni.show_major && alumni.major && (
          <div className="flex items-center">
            <FiBookOpen className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{alumni.major}</span>
          </div>
        )}
        {alumni.show_degree_type && alumni.degree_type && (
          <div className="flex items-center">
            <FiAward className="w-3 h-3 mr-1 flex-shrink-0" />
            <span>{alumni.degree_type}</span>
          </div>
        )}
        {alumni.show_university && alumni.university && (
          <div className="flex items-center">
            <FiBookOpen className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{alumni.university}</span>
          </div>
        )}
        {alumni.show_location && alumni.location && (
          <div className="flex items-center">
            <FiMapPin className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{alumni.location}</span>
          </div>
        )}
      </div>

      {/* About section (if visible and available) */}
      {alumni.show_about && alumni.about && (
        <div className="mb-3">
          <p className="text-xs text-gray-700 line-clamp-2">
            {alumni.about.length > 80 ? `${alumni.about.substring(0, 80)}...` : alumni.about}
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => onViewProfile(alumni.id)}
          className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-blue-700 transition-colors"
        >
          View Profile
        </button>
        {onConnect && (
          <button
            onClick={() => onConnect(alumni.id)}
            className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded text-xs font-medium hover:bg-gray-200 transition-colors"
          >
            Connect
          </button>
        )}
        {onMessage && (
          <button
            onClick={() => onMessage(alumni.id)}
            className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            <FiMail className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile card component for job postings
 */
export const MobileJobCard = ({ job, onApply, onViewDetails, onSave }) => {
  const formatSalary = (min, max, currency = 'USD', period = 'annual') => {
    if (!min && !max) return null;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}`;
    } else if (min) {
      return `From ${formatter.format(min)}`;
    } else {
      return `Up to ${formatter.format(max)}`;
    }
  };

  const getEmploymentTypeColor = (type) => {
    const colors = {
      'full-time': 'bg-blue-100 text-blue-800',
      'part-time': 'bg-green-100 text-green-800',
      'contract': 'bg-yellow-100 text-yellow-800',
      'internship': 'bg-purple-100 text-purple-800',
      'temporary': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4 border-l-4 border-blue-500">
      {/* Job header */}
      <div className="mb-2">
        <div className="flex justify-between items-start">
          <h3 className="font-semibold text-gray-900 text-sm flex-1 pr-2">
            {job.title}
          </h3>
          {job.posted_date && (
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {new Date(job.posted_date).toLocaleDateString()}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">{job.company?.name || job.company_name}</p>
      </div>

      {/* Job details */}
      <div className="space-y-1 mb-3">
        {/* Location */}
        <div className="flex items-center text-xs text-gray-500">
          <FiMapPin className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">
            {job.remote_type === 'remote' ? 'Remote' : 
             job.city && job.state ? `${job.city}, ${job.state}` :
             job.location || 'Location not specified'}
          </span>
        </div>

        {/* Salary */}
        {job.show_salary && (job.salary_min || job.salary_max) && (
          <div className="flex items-center text-xs text-gray-500">
            <FiDollarSign className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">
              {formatSalary(job.salary_min, job.salary_max, job.salary_currency, job.salary_period)}
            </span>
          </div>
        )}

        {/* Experience level */}
        {job.experience_level && (
          <div className="flex items-center text-xs text-gray-500">
            <FiAward className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="capitalize">{job.experience_level} Level</span>
          </div>
        )}
      </div>

      {/* Employment type badge */}
      {job.employment_type && (
        <div className="mb-3">
          <span className={`inline-block text-xs px-2 py-1 rounded ${getEmploymentTypeColor(job.employment_type)}`}>
            {job.employment_type.split('-').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')}
          </span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {job.application_method === 'external' ? (
          <a
            href={job.application_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            Apply <FiExternalLink className="w-3 h-3 ml-1" />
          </a>
        ) : (
          <button
            onClick={() => onApply(job.id)}
            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            Apply Now
          </button>
        )}
        <button
          onClick={() => onViewDetails(job.id)}
          className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded text-xs font-medium hover:bg-gray-200 transition-colors"
        >
          View Details
        </button>
        {onSave && (
          <button
            onClick={() => onSave(job.id)}
            className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            <FiBookOpen className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile card component for messages/conversations
 */
export const MobileMessageCard = ({ message, onClick, isUnread }) => {
  const getTimeAgo = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now - messageDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return messageDate.toLocaleDateString();
  };

  return (
    <div 
      onClick={() => onClick(message.id)}
      className={`bg-white rounded-lg shadow-sm p-3 mb-2 cursor-pointer transition-all hover:shadow-md ${
        isUnread ? 'border-l-4 border-blue-500 bg-blue-50' : ''
      }`}
    >
      <div className="flex items-start">
        {/* Avatar */}
        <div className="flex-shrink-0 mr-3">
          {message.sender_avatar ? (
            <img 
              src={message.sender_avatar} 
              alt={message.sender_name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
              <FiUser className="w-5 h-5 text-gray-600" />
            </div>
          )}
        </div>

        {/* Message content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-1">
            <h4 className={`text-sm font-medium text-gray-900 truncate ${
              isUnread ? 'font-semibold' : ''
            }`}>
              {message.sender_name}
            </h4>
            <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
              {getTimeAgo(message.timestamp)}
            </span>
          </div>
          <p className="text-xs text-gray-600 line-clamp-2">
            {message.preview || message.content}
          </p>
          {message.unread_count > 0 && (
            <span className="inline-block mt-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
              {message.unread_count} new
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile card component for groups
 */
export const MobileGroupCard = ({ group, onJoin, onView, isMember }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4">
      {/* Group header */}
      <div className="flex items-start mb-3">
        {group.image_url ? (
          <img 
            src={group.image_url} 
            alt={group.name}
            className="w-12 h-12 rounded-lg object-cover mr-3"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-3">
            <FiUsers className="w-6 h-6 text-blue-600" />
          </div>
        )}
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-sm">{group.name}</h3>
          <p className="text-xs text-gray-500 mt-1">
            {group.member_count} members • {group.post_count} posts
          </p>
        </div>
      </div>

      {/* Group description */}
      {group.description && (
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">
          {group.description}
        </p>
      )}

      {/* Group tags */}
      {group.tags && group.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {group.tags.slice(0, 3).map((tag, index) => (
            <span 
              key={index}
              className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded"
            >
              {tag}
            </span>
          ))}
          {group.tags.length > 3 && (
            <span className="text-xs text-gray-500">+{group.tags.length - 3} more</span>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => onView(group.id)}
          className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded text-xs font-medium hover:bg-gray-200 transition-colors"
        >
          View Group
        </button>
        {!isMember && (
          <button
            onClick={() => onJoin(group.id)}
            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            Join Group
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile navigation header component
 */
export const MobileAlumniNav = ({ title, onMenuClick, onBackClick, showBack = false }) => {
  return (
    <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        {showBack ? (
          <button
            onClick={onBackClick}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Go back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        ) : (
          <button
            onClick={onMenuClick}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
        
        <div className="w-9" /> {/* Spacer for centering */}
      </div>
    </div>
  );
};

/**
 * Mobile bottom navigation for key alumni sections
 */
export const MobileAlumniBottomNav = ({ activeSection, onNavigate }) => {
  const sections = [
    { id: 'dashboard', label: 'Home', icon: FiUser, path: PATHS.dashboard },
    { id: 'jobs', label: 'Jobs', icon: FiBriefcase, path: PATHS.jobBoard },
    { id: 'directory', label: 'Directory', icon: FiUsers, path: PATHS.directory },
    { id: 'messages', label: 'Messages', icon: FiMessageSquare, path: PATHS.messages },
    { id: 'groups', label: 'Groups', icon: FiUsers, path: PATHS.groups }
  ];

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
      <div className="flex justify-around items-center py-2">
        {sections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          
          return (
            <button
              key={section.id}
              onClick={() => onNavigate(section.path)}
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors ${
                isActive 
                  ? 'text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : ''}`} />
              <span className="text-xs mt-1">{section.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Mobile sidebar menu for alumni pages
 */
export const MobileAlumniSidebar = ({ isOpen, onClose, userProfile, onNavigate }) => {
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('alumniData');
    onNavigate(PATHS.login);
  };

  if (!isOpen) return null;

  return (
    <div className="md:hidden fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Sidebar */}
      <div className="relative flex flex-col w-80 max-w-xs bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* User Profile Section */}
        {userProfile && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {userProfile.headshot_url ? (
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={userProfile.headshot_url}
                    alt={`${userProfile.first_name} ${userProfile.last_name}`}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                    <FiUser className="h-5 w-5 text-gray-600" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {userProfile.first_name} {userProfile.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {userProfile.current_employer || 'Alumni Member'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => {
              onNavigate(PATHS.dashboard);
              onClose();
            }}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiUser className="mr-3 h-5 w-5" />
            Dashboard
          </button>
          <button
            onClick={() => {
              onNavigate(PATHS.jobBoard);
              onClose();
            }}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiBriefcase className="mr-3 h-5 w-5" />
            Job Board
          </button>
          <button
            onClick={() => {
              onNavigate(PATHS.directory);
              onClose();
            }}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiUsers className="mr-3 h-5 w-5" />
            Alumni Directory
          </button>
          <button
            onClick={() => {
              onNavigate(PATHS.messages);
              onClose();
            }}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiMessageSquare className="mr-3 h-5 w-5" />
            Messages
          </button>
          <button
            onClick={() => {
              onNavigate(PATHS.groups);
              onClose();
            }}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiUsers className="mr-3 h-5 w-5" />
            Alumni Groups
          </button>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          >
            <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};